import React from 'react';
import { connect } from 'react-redux';

import { sendResultsAndActivate } from '../../../api/quizlerApi';
import * as eventsApi from '../../../api/eventsApi';
import tagular from '../../../util/tagular';
import { propExistsAndNonEmpty } from '../../../util/utils';
import { getEmailBodyStrategy } from '../../../strategies/emailBodyStrategies/emailBodyStrategyFactory';
import { getSortStrategy } from '../../../strategies/sortStrategies/sortStrategyFactory';

import Toggle from '../../toggle/toggle';
import DrawerSectionContainer from '../../shared/drawerSectionContainer/drawerSectionContainer';
import NewsletterOptIn from './newsletterOptIn/newsletterOptIn';
import Consent from './consent/consent';

import './emailDropdown.scss';

const HEALTHLINE_PILLAR_ID = 2;

class EmailDropdown extends React.Component {
    constructor(props) {
        super(props);

        const { resultsConfig } = props;

        let moduleExpanded = true;
        let submitCTA = 'SEND MY RESULTS';
        let formHeading = 'Send me my results';
        if (resultsConfig.emailSend) {
            const { defaultExpand, cta, heading } = resultsConfig.emailSend;
            if (typeof defaultExpand !== 'undefined') {
                moduleExpanded = defaultExpand;
            }
            if (typeof cta !== 'undefined') {
                submitCTA = cta;
            }
            if (typeof heading !== 'undefined') {
                formHeading = heading;
            }
        }

        this.state = {
            emailValue: '',
            moduleExpanded,
            optInLists: [],
            done: false,
            submitCTA,
            errorText: '',
            errorClass: '',
            formHeading,
            consentChecked: false,
            consentError: false
        };

        this.handleEmailChange = this.handleEmailChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleOptIn = this.handleOptIn.bind(this);
        this.handleSectionClick = this.handleSectionClick.bind(this);
        this.handleConsent = this.handleConsent.bind(this);
    }

    getEmailSendInfo() {
        return { ...this.props.resultsConfig?.emailSend, ...this.props.filteredResults[0]?.emailSend };
    }

    componentDidUpdate(prevProps) {
        const updates = {};

        if (this.props.filteredResults !== prevProps.filteredResults) {
            updates.done = false;
            updates.optInLists = [];

            const resultEmailSend = this.props.filteredResults[0]?.emailSend
            if (resultEmailSend) {
                const { heading, cta } = resultEmailSend;
                if (heading !== undefined) {
                    updates.formHeading = heading;
                }
                if (cta !== undefined) {
                    updates.submitCTA = cta;
                }
            }
        }

        if (this.props.resultsConfig.emailSend !== prevProps.resultsConfig.emailSend) {
            const emailSendInfo = this.getEmailSendInfo();

            if (emailSendInfo.defaultExpand !== undefined) {
                updates.moduleExpanded = emailSendInfo.defaultExpand ;
            }
            if (emailSendInfo.cta !== undefined) {
                updates.submitCTA = emailSendInfo.cta ;
            }
        }

        if (Object.keys(updates).length) {
            this.setState(updates);
        }
    }

    handleEmailChange(e) {
        this.setState({ emailValue: e.target.value });
    }

    handleOptIn(list) {
        this.setState({
            optInLists: [...this.state.optInLists, list]
        });
    }

    handleConsent() {
        this.setState({
            consentChecked: !this.state.consentChecked,
            consentError: false
        });
    }

    async handleSubmit(e) {
        e.preventDefault();

        const { assessment } = this.props;

        const emailPayload = this.constructEmailPayload();

        if (await this.validate(emailPayload)) {
            sendResultsAndActivate(emailPayload);

            eventsApi.emailSubmitted(assessment, { text: this.state.submitCTA });

            tagular.sendProductClickedEvent(e, {
                assessmentName: assessment.internalName,
                location: 'CARDS',
                formatType: 'BUTTON',
                text: this.state.submitCTA,
                name: emailPayload.lists.join(','),
                category: 'Newsletter Acquisition'
            });

            this.setState({
                done: true,
                submitCTA: 'SEND MY NEW RESULTS'
            });
        }
    }

    handleSectionClick(e, toggle) {
        toggle();

        const isCurrentlyExpanded = this.state.moduleExpanded;

        this.setState({ moduleExpanded: !isCurrentlyExpanded });

        if (!isCurrentlyExpanded) {
            eventsApi.expandClicked(this.props.assessment);

            tagular.sendElementClickedEvent(e, {
                position: 'Article Body',
                elementType: 'BUTTON',
                action: 'EXPAND'
            });
        }
    }

    constructEmailPayload() {
        const {
            resultsConfig,
            filteredResults,
            prompts,
            assessment,
            countryInfo,
            selectedFiltersArray
        } = this.props;
        const {
            saveResult,
            primaryResultTrackingVarKey,
            secondaryResultTrackingVarKey,
            consentTrackingVarKey,
            consentTrackingVarValue
        } = resultsConfig;

        const emailSend = this.getEmailSendInfo();

        const email = this.state.emailValue;

        const sort = getSortStrategy(resultsConfig);
        const sortedResults = sort(filteredResults, selectedFiltersArray);

        const constructBody = getEmailBodyStrategy(resultsConfig.displayType);
        const results = constructBody(prompts, sortedResults);

        const lists = emailSend.lists.concat(this.state.optInLists);

        const { template } = emailSend;
        const source = emailSend.source || 'quizzler';
        const { condition } = assessment;
        const resultsUrl = window.location.href;
        const { site } = assessment;

        const trackingVars = { source };

        if (saveResult) {
            if (countryInfo != 'US') {
                if (consentTrackingVarKey)
                    trackingVars[consentTrackingVarKey] = consentTrackingVarValue;
            }

            if (primaryResultTrackingVarKey) {
                trackingVars[primaryResultTrackingVarKey] = results[0].title;
            }
            if (secondaryResultTrackingVarKey) {
                trackingVars[secondaryResultTrackingVarKey] = results[1].title;
            }
        }

        return {
            email,
            results,
            template,
            trackingVars,
            lists,
            resultsUrl,
            condition,
            site
        };
    }

    async validate(emailPayload) {
        let isValid = true;
        const { resultsConfig } = this.props;

        if (resultsConfig.saveResult) {
            const { countryInfo } = this.props;
            if (countryInfo.countryCode != 'US' && !this.state.consentChecked) {
                isValid = false;
                this.setState({
                    consentError: true
                });
                return isValid;
            }

            this.setState({
                consentError: false
            });
        }

        const pattern = new RegExp(
            /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
        );
        if (!pattern.test(emailPayload.email)) {
            isValid = false;
        }

        if (!isValid) {
            this.setState({
                errorText: 'Please enter a valid email address.',
                errorClass: 'invalidInputRedBorder'
            });
        }
        return isValid;
    }

    render() {
        const { assessment, resultsConfig, countryInfo } = this.props;
        const applyConsent = countryInfo != 'US' && resultsConfig.saveResult;

        if (!resultsConfig.emailSend) {
            return null;
        }

        let screen;
        let expandAsDefault = true;
        let disableExpand = false;

        if (propExistsAndNonEmpty(assessment)) {
            let emailSendInfo = this.getEmailSendInfo();

            if (Object.keys(emailSendInfo).length === 0) {
                emailSendInfo = {
                    description:
                        'Enter your email and we’ll send you your results to refer to any time.',
                    lists: ['Nutrition'],
                    optionalLists: [],
                    template: 'diet-finder-results'
                };
            }

            if (emailSendInfo.defaultExpand !== undefined) {
                expandAsDefault = assessment.resultsConfig.emailSend.defaultExpand;
            }

            disableExpand = emailSendInfo?.disableExpand;

            const displayType = assessment.resultsConfig
                ? assessment.resultsConfig.displayType
                : '';

            const form = (
                <form onSubmit={this.handleSubmit} className={displayType}>
                    <p dangerouslySetInnerHTML={{ __html: emailSendInfo.description }} />

                    <div className={`submission-field ${assessment.site}`}>
                        <label>
                            <input
                                id="emailInput"
                                type="text"
                                className={this.state.errorClass}
                                value={this.state.emailValue}
                                placeholder={
                                    this.state.emailValue === ''
                                        ? 'Enter your email'
                                        : this.state.emailValue
                                }
                                onChange={this.handleEmailChange}
                            />
                            <span className="error">{this.state.errorText}</span>
                        </label>
                        <input
                            id="submit"
                            type="submit"
                            className={`button`}
                            value={this.state.submitCTA}
                        />
                    </div>

                    {applyConsent && (
                        <Consent
                            privacyPolicyUrl={assessment.privacyPolicyUrl}
                            handleConsent={this.handleConsent}
                            consentError={this.state.consentError}
                        />
                    )}

                    {emailSendInfo.optionalLists && emailSendInfo.optionalLists.length > 0
                        ? emailSendInfo.optionalLists.map(list => (
                            <NewsletterOptIn
                                newsletterList={list}
                                key={list}
                                handleOptIn={this.handleOptIn}
                            />
                        ))
                        : null}

                    <a
                        href={assessment.privacyPolicyUrl}
                        target="_blank"
                        className="privacy-text"
                        rel="noreferrer"
                    >
                        Your <span className="privacy">privacy</span> is important to us
                    </a>
                </form>
            );

            const confirmationMessage = emailSendInfo.confirmationMessage || 'Check your email for your results.';

            const confirmation = (
                <div>
                    <h4>Thank you!</h4>
                    <p>{confirmationMessage}</p>
                </div>
            );

            screen = this.state.done ? confirmation : form;
        }

        return (
            <Toggle expandAsDefault={expandAsDefault}>
                {({ on, toggle }) => (
                    <div className="content-container border email-container">
                        <div className="email">
                            {!!disableExpand ? (
                                <span className="heading">{this.state.formHeading}</span>
                            ) : (
                                <DrawerSectionContainer
                                    moduleExpanded={this.state.moduleExpanded}
                                    handleClick={e => this.handleSectionClick(e, toggle)}
                                    site={assessment.site}
                                >
                                    <span className={`heading pointer ${assessment.site}`}>{this.state.formHeading}</span>
                                </DrawerSectionContainer>
                            )}

                            {on && screen}
                        </div>
                    </div>
                )}
            </Toggle>
        );
    }
}

const mapStateToProps = state => {
    return {
        assessment: state.app.assessment,
        resultsConfig: state.app.assessment.resultsConfig || {},
        prompts: state.entities.prompts,
        filteredResults: state.session.filteredResults,
        selectedFiltersArray: state.session.selectedFilters.inResults,
        countryInfo: state.app.countryInfo
    };
};

export default connect(mapStateToProps, null)(EmailDropdown);
